import { zodResolver } from '@hookform/resolvers/zod';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckIcon from '@mui/icons-material/CheckCircle';
import LinkIcon from '@mui/icons-material/LinkOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import type { Theme } from '@mui/material/styles';
import {
  alpha,
  Box,
  GlassAlert,
  GlassButton,
  GlassTextField,
  iconSizes,
  Link,
  ShimmerLoader,
  Typography,
  useI18n,
  useTerminology,
} from '@ops-matrix/design-system-ui';
import { sendPasswordResetEmail } from 'firebase/auth';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { auth } from '../config/firebase';

export default function ForgotPasswordPage() {
  const { t } = useI18n();
  const { term } = useTerminology();
  // Platform name comes from tenant terminology overrides — falls back to the
  // `navigation.platformName` i18n value (`'Fiops'` in en.ts). Tenants override
  // this via their business.terminology config (§11d white-label rules).
  const platformName = term('navigation.platformName');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailSent, setEmailSent] = useState(false);

  const forgotPasswordSchema = z.object({
    email: z.string().email(t('validation.email')),
  });

  type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  const onSubmit = async (data: ForgotPasswordFormData) => {
    setIsLoading(true);
    setError(null);

    try {
      await sendPasswordResetEmail(auth, data.email);
      setEmailSent(true);
    } catch (err) {
      const firebaseError = err as { code?: string };
      if (firebaseError.code === 'auth/user-not-found') {
        // Don't reveal if user exists — surface the same "check your email"
        // success state so attackers can't enumerate registered emails.
        setEmailSent(true);
      } else {
        setError(t('auth.forgotPassword.error'));
        console.error('Password reset error:', err);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const currentParams = searchParams.toString();
  const queryString = currentParams ? `?${currentParams}` : '';

  if (emailSent) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', py: 2 }}>
        <Box
          sx={(theme: Theme) => ({
            width: 56,
            height: 56,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.success.main, 0.15),
            border: '1px solid',
            borderColor: alpha(theme.palette.success.main, 0.3),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
          })}
        >
          <CheckIcon sx={{ fontSize: iconSizes.xl, color: 'success.main' }} />
        </Box>
        <Typography variant="h3" sx={{ fontWeight: 600, letterSpacing: '-0.01em', mb: 0.75 }}>
          {t('auth.forgotPassword.checkEmail')}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.55, mb: 3, maxWidth: 360 }}>
          {t('auth.forgotPassword.checkEmailDescription')}
        </Typography>
        <RouterLink to={`/login${queryString}`} style={{ textDecoration: 'none' }}>
          <GlassButton variant="contained" color="success">
            {t('auth.forgotPassword.backToSignIn')}
          </GlassButton>
        </RouterLink>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {/* Page header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" sx={{ fontWeight: 600, letterSpacing: '-0.01em', mb: 0.75 }}>
          {t('auth.forgotPassword.titleV3')}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
          {t('auth.forgotPassword.subtitleV3', { platformName })}
        </Typography>
      </Box>

      <GlassAlert severity="error" visible={!!error} sx={{ mb: 2 }}>
        {error}
      </GlassAlert>

      <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ display: 'flex', flexDirection: 'column' }}>
        {/* Work email field */}
        <GlassTextField
          {...register('email')}
          label={t('auth.forgotPassword.workEmailLabel')}
          type="email"
          autoComplete="email"
          autoFocus
          disabled={isLoading}
          error={!!errors.email}
          helperText={errors.email?.message}
          fullWidth
          sx={{ mb: 2.5 }}
        />

        {/* Primary send-reset-link button */}
        <GlassButton
          type="submit"
          variant="contained"
          color="success"
          fullWidth
          size="large"
          disabled={isLoading}
          endIcon={!isLoading ? <ArrowForwardIcon /> : undefined}
        >
          {isLoading ? (
            <ShimmerLoader variant="circular" width={24} height={24} />
          ) : (
            t('auth.forgotPassword.sendResetLink')
          )}
        </GlassButton>
      </Box>

      {/* Mono divider "OR" */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, my: 2.5 }}>
        <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
        <Typography
          variant="overline"
          sx={{
            color: 'text.disabled',
            fontFamily: 'ui-monospace, "SF Mono", Menlo, Monaco, Consolas, monospace',
            letterSpacing: '0.1em',
          }}
        >
          {t('auth.forgotPassword.orDivider')}
        </Typography>
        <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
      </Box>

      {/* Alt path: magic link */}
      <GlassButton
        variant="subtle"
        fullWidth
        size="large"
        startIcon={<LinkIcon sx={{ color: 'success.main' }} />}
        onClick={() => navigate(`/magic-link${queryString}`)}
        disabled={isLoading}
      >
        {t('auth.forgotPassword.useMagicLinkInstead')}
      </GlassButton>

      {/* Admin-recovery callout — amber-tinted glass panel with shield icon */}
      <Box
        sx={(theme: Theme) => ({
          mt: 2.5,
          p: 1.75,
          display: 'flex',
          gap: 1.25,
          bgcolor: alpha(theme.palette.background.paper, 0.6),
          border: '1px solid',
          borderColor: alpha(theme.palette.warning.main, 0.25),
          borderRadius: 1.5,
        })}
      >
        <ShieldOutlinedIcon
          sx={(theme: Theme) => ({
            fontSize: iconSizes.sm,
            color: theme.palette.warning.main,
            flexShrink: 0,
            mt: '2px',
          })}
        />
        <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.55 }}>
          {t('auth.forgotPassword.adminRecoveryBefore')}
          <Typography component="span" variant="caption" sx={{ color: 'text.primary', fontWeight: 600 }}>
            {t('auth.forgotPassword.adminLabel')}
          </Typography>
          {t('auth.forgotPassword.adminRecoveryAfter')}
        </Typography>
      </Box>

      {/* Footer */}
      <Box sx={{ mt: 3.5, pt: 2.5, borderTop: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {t('auth.forgotPassword.rememberItPrompt')}{' '}
          <Link component={RouterLink} to={`/login${queryString}`} sx={{ color: 'success.main', fontWeight: 600 }}>
            {t('auth.signIn')} →
          </Link>
        </Typography>
      </Box>
    </Box>
  );
}
